import { BLACK, type Color, KING, SNAIL, TYPE_MASK, WHITE, other, pieceColor, pieceDef, snailCountdown, snailSquare, snailTarget } from '@chessbut/engine';
import type { Decoration, VariantUi } from './ui.ts';

export const snailImage = (c: Color) => `/art/snail-${c === WHITE ? 'w' : 'b'}.svg`;

export const snailUi: VariantUi = {
  heroImage: '/art/snail-w.svg',
  peek: [{}, {}, { image: '/art/bK.svg', threat: true }, {}, { image: '/art/snail-w.svg' }, {}, {}, {}, {}],

  twist(pos, you) {
    const me: Color = you ?? pos.turn;
    const them = other(me);
    // About to lose a piece (or the king) to the enemy snail?
    const theirTarget = snailTarget(pos.ext!, pos.royals, them);
    if (theirTarget >= 0 && snailCountdown(pos, them) === 1) {
      const victim = pos.board[theirTarget];
      if (victim && pieceColor(victim) === me && (victim & TYPE_MASK) !== KING) {
        return { text: `${you === null ? 'A' : 'Your'} ${pieceDef(victim & TYPE_MASK).name} is lunch next move`, alert: true };
      }
    }
    const myTarget = snailTarget(pos.ext!, pos.royals, me);
    if (myTarget >= 0 && snailCountdown(pos, me) === 1) {
      const victim = pos.board[myTarget];
      if (victim && pieceColor(victim) === them && (victim & TYPE_MASK) !== KING) {
        return { text: `${you === null ? 'The' : 'Your'} snail eats a ${pieceDef(victim & TYPE_MASK).name} next move`, pips: { on: 2, total: 3, title: 'Moves until your snail crawls' } };
      }
    }
    const n = snailCountdown(pos, me);
    return {
      text: n === 1 ? `${you === null ? 'Snail' : 'Your snail'} crawls after the next move` : `${you === null ? 'Snail' : 'Your snail'} crawls in ${n} moves`,
      pips: { on: 3 - n, total: 3, title: 'Moves until your snail crawls' },
    };
  },

  pieceImage(pos, sq, piece) {
    if ((piece & TYPE_MASK) !== SNAIL) return null;
    return snailImage(snailSquare(pos, WHITE) === sq ? WHITE : BLACK);
  },

  pieceLabel(pos, sq, piece) {
    if ((piece & TYPE_MASK) !== SNAIL) return null;
    return snailSquare(pos, WHITE) === sq ? 'white’s snail' : 'black’s snail';
  },

  decorations(pos) {
    const out: Decoration[] = [];
    for (const c of [WHITE, BLACK] as Color[]) {
      const s = snailSquare(pos, c);
      if (s < 0) continue;
      const n = snailCountdown(pos, c);
      out.push({ kind: 'badge', sq: s, text: String(n), tone: n === 1 ? 'danger' : 'normal' });
      const t = snailTarget(pos.ext!, pos.royals, c);
      if (t < 0) continue;
      const soon = n === 1;
      out.push({ kind: 'arrow', from: s, to: t, tone: soon ? 'danger' : c === WHITE ? 'light' : 'dark', hint: true });
      if (soon) {
        const victim = pos.board[t];
        out.push({ kind: 'mark', sq: t, tone: victim && (victim & TYPE_MASK) !== KING ? 'doomed' : 'danger', hint: true });
      }
    }
    return out;
  },

  playerNote(pos, c) {
    if (snailSquare(pos, c) < 0) return null;
    const n = snailCountdown(pos, c);
    return {
      icon: snailImage(c),
      text: n === 1 ? 'crawls next move' : `crawls in ${n}`,
      tone: n === 1 ? 'danger' : undefined,
      title: "Moves until this player's snail crawls",
    };
  },

  threatText: (you, name) => (you ? 'The snail is coming! Move your king out of its path.' : `${name}'s king must flee the snail!`),

  eventSound(e) {
    if (e.t === 'move' && (e.piece & TYPE_MASK) === SNAIL) return 'crawl';
    if (e.t === 'remove' && e.cause === 'snail') return 'eat';
    return null;
  },

  moveBadge(rec) {
    const crawl = rec.events.find((e) => e.t === 'move' && (e.piece & TYPE_MASK) === SNAIL);
    if (!crawl) return null;
    const eaten = rec.events.find((e) => e.t === 'remove' && e.cause === 'snail');
    return {
      icon: snailImage(rec.color),
      victim: eaten && eaten.t === 'remove' ? eaten.piece : undefined,
      title: eaten ? 'The snail ate a piece' : 'The snail crawled',
    };
  },
};
